import type { IngestBatch, WireEvent } from "@tracking-web/shared";
import { uid } from "./util.js";

/**
 * Cola de eventos con persistencia y reintentos (Bloque B2). Núcleo PURO: no toca
 * el navegador directamente — recibe storage/postJson/beacon/now inyectados, así que
 * es testeable de cabo a rabo y mobile-safe por construcción. El manejo específico
 * del navegador (try/catch de localStorage, visibilitychange/pagehide, online) lo
 * pone transport.ts.
 *
 * Garantías:
 *  - Camino feliz (red OK): CERO escrituras a storage.
 *  - Fallo de red: el lote se persiste y se reintenta con backoff exponencial+jitter.
 *  - Cierre de página: write-ahead (persistir ANTES de sendBeacon, que no confirma)
 *    → se drena en la siguiente carga. El servidor deduplica por eventId (B1).
 *  - active_time NO se reintenta (su `inc` es incremental → recontaría); el resto
 *    (incluido el experiment_goal de time_on_page) SÍ.
 *  - Si el storage se llena: se descartan lotes de VANIDAD antes que los PROTEGIDOS
 *    (experimento / section_time): ambos son métrica de dinero y se deduplican por
 *    eventId, así que reentregarlos es seguro y perderlos sí duele.
 */

/** Storage mínimo (lo implementa transport sobre localStorage, con try/catch). */
export type StorageLike = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  keys(): string[];
};

export type QueueOpts = {
  /** Envelope del lote (siteKey/sessionId/anonId/meta de ESTA carga de página). */
  envelope: Pick<IngestBatch, "k" | "s" | "a" | "m">;
  /** Persiste; null = incógnito/bloqueado → solo memoria (degradación elegante). */
  storage: StorageLike | null;
  /** Envío observable (fetch). Resuelve true SOLO si el servidor confirmó (2xx). */
  postJson: (body: string) => Promise<boolean>;
  /** sendBeacon: true = el navegador lo encoló (NO confirma entrega). */
  beacon: (body: string) => boolean;
  now: () => number;
  /** Aleatorio [0,1) para el jitter (inyectable para tests). */
  rand?: () => number;
  keyPrefix?: string;
  maxBytes?: number;
  backoffBaseMs?: number;
  backoffMaxMs?: number;
};

type Persisted = { body: string; n: number; t: number; x: boolean }; // body, attempts, nextAttempt, isProtected

// Eventos PROTEGIDOS de la evicción por cuota: experimentos (exposición/conversión) y
// section_time (tramos del mapa de atención). Son métrica de dinero y se deduplican por
// eventId en el servidor → reentregarlos es idempotente; perderlos corrompe el dato.
const isProtected = (e: WireEvent) =>
  e.t.indexOf("experiment_") === 0 || e.t === "section_time";

export function createQueue(opts: QueueOpts) {
  const storage = opts.storage;
  const now = opts.now;
  const rand = opts.rand ?? Math.random;
  const prefix = opts.keyPrefix ?? "tw_q:";
  const maxBytes = opts.maxBytes ?? 50_000;
  const base = opts.backoffBaseMs ?? 2_000;
  const cap = opts.backoffMaxMs ?? 60_000;

  let buffer: WireEvent[] = [];
  // Bandera en memoria: evita tocar storage en el camino feliz. Se enciende al
  // persistir y al arrancar si ya hay lotes de una sesión anterior.
  let hasPersisted = scanHasPersisted();

  function scanHasPersisted(): boolean {
    if (!storage) return false;
    try {
      return storage.keys().some((k) => k.indexOf(prefix) === 0);
    } catch {
      return false;
    }
  }

  function buildBody(events: WireEvent[]): string {
    const batch: IngestBatch = { ...opts.envelope, e: events };
    return JSON.stringify(batch);
  }

  function backoff(attempts: number): number {
    const delay = Math.min(cap, base * Math.pow(2, attempts - 1));
    return delay + rand() * delay; // full-ish jitter (evita reintentos sincronizados)
  }

  function persistedKeys(): string[] {
    if (!storage) return [];
    try {
      return storage.keys().filter((k) => k.indexOf(prefix) === 0);
    } catch {
      return [];
    }
  }

  function readRec(key: string): Persisted | null {
    try {
      const raw = storage!.getItem(key);
      return raw ? (JSON.parse(raw) as Persisted) : null;
    } catch {
      return null;
    }
  }

  /** Persiste un lote para reintento. Quita active_time (no reintentable). */
  function persist(events: WireEvent[]): void {
    if (!storage) return; // sin storage: degradación a memoria (se pierde al cerrar)
    const retryable = events.filter((e) => e.t !== "active_time");
    if (retryable.length === 0) return;
    const body = buildBody(retryable);
    const rec: Persisted = { body, n: 1, t: now() + backoff(1), x: retryable.some(isProtected) };
    const value = JSON.stringify(rec);
    evictToFit(value.length + prefix.length + 8);
    try {
      storage.setItem(prefix + uid(), value);
      hasPersisted = true;
    } catch {
      /* cuota/incógnito: no rompemos nada; se pierde este lote */
    }
  }

  /** Hace sitio para `needed` bytes: descarta vanidad (oldest-first), luego experimento. */
  function evictToFit(needed: number): void {
    if (!storage) return;
    let recs = persistedKeys()
      .map((key) => ({ key, rec: readRec(key) }))
      .filter((r): r is { key: string; rec: Persisted } => !!r.rec);
    const bytes = () => recs.reduce((s, r) => s + r.rec.body.length, 0);
    if (bytes() + needed <= maxBytes) return;
    // Orden de descarte: vanidad antes que protegido; dentro, el más viejo (menor t).
    recs.sort((a, b) => (a.rec.x === b.rec.x ? a.rec.t - b.rec.t : a.rec.x ? 1 : -1));
    for (const r of recs) {
      if (bytes() + needed <= maxBytes) break;
      try {
        storage.removeItem(r.key);
      } catch {
        /* ignore */
      }
      recs = recs.filter((x) => x.key !== r.key);
    }
  }

  /** Reintenta los lotes persistidos cuyo nextAttempt ya venció. */
  async function drain(): Promise<void> {
    if (!storage || !hasPersisted) return;
    const keys = persistedKeys();
    if (keys.length === 0) {
      hasPersisted = false;
      return;
    }
    const t = now();
    for (const key of keys) {
      const rec = readRec(key);
      if (!rec) {
        try { storage.removeItem(key); } catch { /* ignore */ }
        continue;
      }
      if (rec.t > t) continue; // aún en backoff
      let ok = false;
      try {
        ok = await opts.postJson(rec.body);
      } catch {
        ok = false;
      }
      if (ok) {
        try { storage.removeItem(key); } catch { /* ignore */ }
      } else {
        rec.n += 1;
        rec.t = now() + backoff(rec.n);
        try { storage.setItem(key, JSON.stringify(rec)); } catch { /* ignore */ }
      }
    }
    hasPersisted = persistedKeys().length > 0;
  }

  /** Encola un evento (ya con su eventId asignado por transport). */
  function add(event: WireEvent): void {
    buffer.push(event);
  }

  /** Flush activo (página viva): envía con fetch observable; si falla, persiste.
   *  Después drena lo pendiente (solo si lo hay → cero storage en camino feliz). */
  async function flushActive(): Promise<void> {
    const events = buffer;
    buffer = [];
    if (events.length > 0) {
      let ok = false;
      try {
        ok = await opts.postJson(buildBody(events));
      } catch {
        ok = false;
      }
      if (!ok) persist(events);
    }
    if (hasPersisted) await drain();
  }

  /** Flush de cierre (visibilitychange→hidden / pagehide): write-ahead (persistir
   *  ANTES) + sendBeacon best-effort. No se confirma → se drena en la próxima carga. */
  function flushOnClose(): void {
    const events = buffer;
    buffer = [];
    if (events.length === 0) return;
    persist(events); // write-ahead: si la página muere, queda guardado
    try {
      opts.beacon(buildBody(events));
    } catch {
      /* da igual: ya está persistido para el próximo drain */
    }
  }

  return { add, flushActive, flushOnClose, drain, _hasPersisted: () => hasPersisted };
}
